import React, { useEffect, useRef, useState, memo, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';

import DoneIcon from '@mui/icons-material/Done';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { get as _get } from 'lodash';
import PopupState, { bindHover, bindMenu } from 'material-ui-popup-state';
import HoverMenu from 'material-ui-popup-state/HoverMenu';

// import withWhyRender, { useWhatChangedEffect } from '../libraries/withwhyrender';

const useStyles = makeStyles((theme) => ({
    popper: {
        zIndex: theme.zIndex.snackbar,
        background: theme.palette.common.white,
        color: theme.palette.common.black,
        direction: 'ltr',
    },
    paper: {
        background: theme.palette.common.white,
        color: theme.palette.common.black,
    },
    list: {
        maxHeight: 350,
        overflowY: 'auto'
    },
    divider: {
        height: 1,
        backgroundColor: theme.palette.common.black,
        marginLeft: 15,
        marginRight: 15,
        marginTop: 10,
        marginBottom: 10,
        opacity: 0.3,
    },
    listitem: {
        height: 27,
        fontSize: 12,
        background: theme.palette.common.white,
        textTransform: 'uppercase',
        padding: '6px 16px',
        '&:hover': {
            fontWeight: 'bold'
        },

        '& svg': {
            marginRight: 5,
        },
    },
    iconButton: {
        margin: 0,
    },
    textButton: {
        margin: 0,
        borderRadius: 0,
        borderColor: `${alpha(theme.palette.text.primary, 0.23)}`,
    },
    itemAction: {
        minWidth: 'unset',
        paddingRight: 10,
    },
    search: {
        background: theme.palette.background.paper,
    },
    searchInput: {
        width: 'calc(100% - 20px)',
        marginLeft: 10
    },
    info: {
        paddingLeft: 20,
        fontSize: 'smaller',
        paddingTop: 2,
        opacity: 0.6,
        // display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        maxWidth: 200,
    },
    check: {
        minWidth: 25,
        paddingLeft: 0, //5,
    },
    checkbox: {
        fill: `${alpha(theme.palette.common.black, 0.6)}`,
    },
    wrap: {
        display: 'inline-block',
        borderStyle: 'solid',
        margin: 5,
        borderWidth: 1,
        borderColor: `${alpha(theme.palette.common.black, 0.15)}`,
    }
}));

const PopupListItem = props => {
    const classes = useStyles();

    return (
        <div className={props.wrap ? classes.wrap : ''}>
            {props.itemnode
                ? React.cloneElement(props.itemnode, {
                    item: props.item,
                    onClick: props.onSelection,
                })
                : <MenuItem
                    style={{ paddingLeft: props.hasSingleSelection ? 6 : undefined }}
                    className={classes.listitem}
                    onClick={props.onSelection}>
                    {props.hasSingleSelection &&
                        <Grid className={classes.check}>
                            {props.isSelected &&
                                <DoneIcon fontSize="small" color="primary" />
                            }
                        </Grid>
                    }
                    {props.multiple &&
                        <Grid>
                            <Checkbox
                                disableRipple
                                checked={props.isSelected}
                                color="primary"
                                icon={<CheckBoxIcon className={classes.checkbox} />} />
                        </Grid>
                    }
                    <Grid container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        wrap='nowrap'>
                        <Grid>{props.item.title}</Grid>
                        <Grid container
                            justifyContent="flex-end"
                            alignItems="center">
                            {props.getinfo &&
                                <Grid className={classes.info}>
                                    {props.getinfo(props.item.data)}
                                </Grid>
                            }
                            {props.getinfo2 &&
                                <Grid className={classes.info}>
                                    {props.getinfo2(props.item.data)}
                                </Grid>
                            }
                            {props.altaction &&
                                <Grid >
                                    <IconButton
                                        onClick={props.onSecondAction}
                                        size="small">
                                        {props.actionicon}
                                    </IconButton>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </MenuItem>
            }
        </div>
    );
};
PopupListItem.propTypes = {
    itemnode: PropTypes.node,
    altaction: PropTypes.func,
    actionicon: PropTypes.node,
    getinfo: PropTypes.func,
    getinfo2: PropTypes.func,
    hasSingleSelection: PropTypes.bool,
    item: PropTypes.shape({
        title: PropTypes.string,
        data: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
        ]),
    }).isRequired,
    isSelected: PropTypes.bool,
    onSelection: PropTypes.func.isRequired,
    onSecondAction: PropTypes.func.isRequired,
    wrap: PropTypes.string,
    multiple: PropTypes.bool,
};

const PopupMenu = memo((props) => {
    const {
        placement: placementProp = 'bottom-start',
        icon = <MenuIcon />,
        style = {},
        disabled = false,
        items,
        itemkey,
        itemTitle,
        onSelect,
        moreActions,
        size,
        button,
        selection,
        altaction,
        getInfo,
        getInfo2,
        multiple,
        onOpen,
        zIndex,
        ...rest
    } = props;

    const classes = useStyles();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [list, setList] = useState([]);
    const [search, setSearch] = useState('');

    const handleSearchChange = useCallback(event => {
        setSearch(event.target.value || '');
        event.preventDefault();
    }, []);

    const handleToggle = useCallback(() => {
        setOpen((prevOpen) => !prevOpen);
    }, []);

    const handleClose = useCallback(event => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    }, []);

    const handleSelection = useCallback((event, sel) => {
        if (event.key === 'Space') return;
        setOpen(current => (multiple || false));
        onSelect && onSelect(sel.data);
    }, [onSelect, multiple]);

    const handleOnSecondAction = useCallback((e, item) => {
        e.stopPropagation();
        setOpen(current => (multiple || false));
        altaction && altaction(item.data);
    }, [altaction, multiple]);

    const handleCallback = useCallback((e, clbk) => {
        e.stopPropagation();
        clbk && clbk();
        setOpen(false);
    }, []);

    const isSelected = useCallback(item => {
        if (!selection) return false;
        const _selection = Array.isArray(selection) ? selection : [selection];

        return _selection.some(s => (
            itemkey ? s === _get(item, itemkey)
                : itemTitle ? s === _get(item, itemTitle)
                    : selection === item));
    }, [selection, itemkey, itemTitle]);

    const handleListKeyDown = useCallback(event => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        }
    }, []);

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = React.useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
        setSearch('');
        onOpen && onOpen(open);
    }, [open, onOpen]);

    useEffect(() => {
        // normalize incoming list
        if (!items || items.length < 1) {
            setList([]);
            return;
        }

        const getKey = item => typeof item === 'object'
            ? itemkey ? _get(item, itemkey)
                : itemTitle ? _get(item, itemTitle) : item.id
            : item;

        const getTitle = item => typeof item === 'object'
            ? item._uniqueTitle ? item._uniqueTitle
                : itemTitle ? _get(item, itemTitle)
                    : itemkey ? _get(item, itemkey) : item.id
            : item;

        // some de-duping attempt
        // typically for metadata to add full variable path if two variables have the same name
        items.reduce((a, c, i) => {
            if (typeof c !== 'object') return a;

            const title = getTitle(c);
            const prev = a[title];
            if (prev) {
                // add secondary dedup info to both titles
                items[i]._uniqueTitle = `${title} (${getKey(c)})`;
                items[prev.index]._uniqueTitle = `${title} (${prev.dedup})`;
            } else {
                items[i]._uniqueTitle = title;
            }
            return { ...a, [title]: { index: i, dedup: getKey(c) } };
        }, {});

        const daItems = items.map(item => ({
            data: item,
            title: getTitle(item),
            key: getKey(item),
        }));
        setList(daItems);
    }, [items, itemTitle, itemkey]);

    return (
        <React.Fragment>
            {button
                // user provided component
                ? React.cloneElement(button, {
                    ref: anchorRef,
                    onClick: handleToggle,
                    disabled,
                    title: props.tooltip,
                })
                : props.starticon ? <Button
                    ref={anchorRef}
                    disabled={disabled}
                    className={props.className}
                    size="small"
                    startIcon={<Icon fontSize="small">{props.starticon}</Icon>}
                    onClick={handleToggle}
                    title={props.tooltip}>
                    <p>{props.title}</p>
                </Button>
                    // button with text no icon
                    : props.title ? <Button
                        disabled={disabled}
                        className={clsx(classes.textButton, props.className)}
                        variant='outlined'
                        color='inherit'
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        title={props.tooltip}>
                        {props.title}
                    </Button>
                        // icon button with user provided icon or default menu icon
                        : <IconButton
                            disabled={disabled}
                            style={style}
                            className={classes.iconButton}
                            variant='outlined'
                            ref={anchorRef}
                            aria-label='open menu'
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            onClick={handleToggle}
                            size={props.size}
                            title={props.tooltip}>
                            {icon}
                        </IconButton>}
            <Popper
                {...rest}
                placement={placementProp}
                className={clsx(
                    classes.popper,
                    props.popperclass || props.className,
                )}
                style={{ zIndex: zIndex || undefined }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined} transition
                modifiers={[
                    { name: 'flip', options: { fallbackPlacements: ['top', 'bottom', 'left', 'right'] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', altAxis: true, padding: 8 } },
                    { name: 'eventListeners', options: { scroll: true, resize: true } },
                ]}>
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}>
                        <Paper className={classes.paper} placement={placementProp}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <div>
                                    {list.length >= 11 && <div className={classes.search}>
                                        <TextField
                                            autoFocus
                                            className={classes.searchInput}
                                            margin="dense"
                                            variant="filled"
                                            value={search}
                                            onChange={handleSearchChange}
                                            placeholder={'Search'}
                                            InputProps={{
                                                'aria-label': 'Search',
                                                spellCheck: false,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </div>}
                                    <MenuList
                                        className={classes.list}
                                        style={{ maxWidth: props.wrap ? 300 : '' }}
                                        autoFocusItem={open}
                                        onKeyDown={handleListKeyDown}>
                                        {(moreActions || []).filter(a => a.callback).map((a, i) =>
                                            <div key={`${a.key}_${i}`}>
                                                <MenuItem
                                                    title={a.title}
                                                    aria-label={a.title}
                                                    className={classes.listitem}
                                                    onClick={a.clickable === false
                                                        ? undefined
                                                        : e => handleCallback(e, a.callback)}>
                                                    {a.icon}{a.title}
                                                </MenuItem>
                                                {(i === moreActions.length - 1 || a.divider) &&
                                                    <Divider
                                                        className={classes.divider}
                                                        component="li" />}
                                            </div>
                                        )}
                                        {list
                                            .filter(item => search.length < 1 || (item.title || '').toLowerCase().includes(search.toLowerCase()))
                                            .map(item =>
                                                <PopupListItem
                                                    key={item.key}
                                                    placement={placementProp}
                                                    disable={disabled}
                                                    {...rest}
                                                    item={item}
                                                    isSelected={isSelected(item.data)}
                                                    onSelection={e => handleSelection(e, item)}
                                                    onSecondAction={e => handleOnSecondAction(e, item)}
                                                    multiple={multiple}
                                                    hasSingleSelection={(!multiple && !!selection)}
                                                    altaction={altaction}
                                                    getinfo={getInfo}
                                                    getinfo2={getInfo2} />
                                            )
                                        }
                                    </MenuList>
                                </div>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </React.Fragment>
    );
});

PopupMenu.propTypes = {
    className: PropTypes.string,
    // optional class for popper
    popperclass: PropTypes.string,
    // item specific props
    items: PropTypes.array.isRequired,
    // when items are objects,
    // key will be used for unique id and for display if not itemTitle provided
    itemkey: PropTypes.string,
    // when items are objects,
    // title will be used for display
    itemTitle: PropTypes.string,
    // selection handler
    onSelect: PropTypes.func,
    // open/closed useCallback
    onOpen: PropTypes.func,
    // node to use for label. default is item key
    // props passed are item, onClick
    itemnode: PropTypes.node,

    // optional addtional action
    altaction: PropTypes.func,
    actionicon: PropTypes.node,

    // optional additional info to display next to item text. Takes a list item
    getInfo: PropTypes.func,
    // 2nd optional additional info to display next to item text. Takes a list item
    getInfo2: PropTypes.func,

    // popup button props
    // popup menu btn icon, default is menu icon
    icon: PropTypes.node,
    // override popup menu button altogether (must be a single React element with no children)
    button: PropTypes.node,
    // popup with button and text (e.g., toolbars)
    starticon: PropTypes.node,
    // just a button with a title
    title: PropTypes.string,
    // popup menu btn size
    size: PropTypes.string,
    // placement
    placement: PropTypes.string,
    // will be applied to the icon
    style: PropTypes.object,
    // tooltip for button
    tooltip: PropTypes.string,
    // custom z-index for the popper
    zIndex: PropTypes.number,

    // optional menu items. expecting [{key, title, callback, <icon>, <divider>}]
    moreActions: PropTypes.array,

    // optional - wrap items when list is long
    wrap: PropTypes.string,

    // current selection (use item key)
    selection: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    disabled: PropTypes.bool,

    // this will add a checkbox to the left of the item
    multiple: PropTypes.bool,

    // used for debuggin
    name: PropTypes.string,
};

// https://github.com/jcoreio/material-ui-popup-state/blob/master/demo/examples/CascadingHoverMenus.js
const useCascadingMenuStyles = makeStyles((theme) => ({
    submenu: {
        marginTop: theme.spacing(-1),
    },
    title: {
        flexGrow: 1,
    },
    moreArrow: {
        marginRight: theme.spacing(-1),
    },
    leftMoreArrow: {
        marginRight: theme.spacing(4),
    },
}));

const CascadingContext = React.createContext({
    parentPopupState: null,
    rootPopupState: null,
});

const CascadingMenuItem = React.forwardRef(({ onClick, ...props }, ref) => {
    const { rootPopupState } = React.useContext(CascadingContext);
    if (!rootPopupState) throw new Error('must be used inside a CascadingMenu');
    const handleClick = useCallback(
        (event) => {
            rootPopupState.close(event);
            if (onClick) onClick(event);
        },
        [rootPopupState, onClick]
    );

    return <MenuItem {...props} onClick={handleClick} />;
});
CascadingMenuItem.displayName = 'CascadingMenuItem';
CascadingMenuItem.propTypes = {
    onClick: PropTypes.func,
};

const CascadingLeftSubmenu = React.forwardRef(({ title, popupId, ...props }, ref) => {
    const classes = useCascadingMenuStyles();
    const { parentPopupState } = React.useContext(CascadingContext);
    return (
        <PopupState
            popupId={popupId}
            variant="popover"
            parentPopupState={parentPopupState}
            disableAutoFocus>
            {(popupState) => (
                <React.Fragment>
                    <MenuItem {...bindHover(popupState)}>
                        <ChevronLeft className={classes.leftMoreArrow} />
                        <span className={classes.title}>{title}</span>
                    </MenuItem>
                    <CascadingMenu
                        {...props}
                        classes={{ ...props.classes, paper: classes.submenu }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        popupState={popupState}
                    />
                </React.Fragment>
            )}
        </PopupState>
    );
});
CascadingLeftSubmenu.displayName = 'CascadingLeftSubmenu';
CascadingLeftSubmenu.propTypes = {
    classes: PropTypes.object,
    title: PropTypes.string,
    popupId: PropTypes.string,
};

const CascadingSubmenu = React.forwardRef(({ title, popupId, ...props }, ref) => {
    const classes = useCascadingMenuStyles();
    const { parentPopupState } = React.useContext(CascadingContext);
    return (
        <PopupState
            popupId={popupId}
            variant="popover"
            parentPopupState={parentPopupState}
            disableAutoFocus>
            {(popupState) => (
                <React.Fragment>
                    <MenuItem {...bindHover(popupState)}>
                        <span className={classes.title}>{title}</span>
                        <ChevronRight className={classes.moreArrow} />
                    </MenuItem>
                    <CascadingMenu
                        {...props}
                        classes={{ ...props.classes, paper: classes.submenu }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        popupState={popupState}
                    />
                </React.Fragment>
            )}
        </PopupState>
    );
});
CascadingSubmenu.displayName = 'CascadingSubmenu';
CascadingSubmenu.propTypes = {
    classes: PropTypes.object,
    title: PropTypes.string,
    popupId: PropTypes.string,
};

const CascadingMenu = ({ popupState, ...props }) => {
    const { rootPopupState } = React.useContext(CascadingContext);
    const context = React.useMemo(
        () => ({
            rootPopupState: rootPopupState || popupState,
            parentPopupState: popupState,
        }),
        [rootPopupState, popupState]
    );

    return (
        <CascadingContext.Provider value={context}>
            <HoverMenu {...props} {...bindMenu(popupState)} />
        </CascadingContext.Provider>
    );
};
CascadingMenu.propTypes = {
    popupState: PropTypes.object,
};

export {
    PopupMenu as default,
    CascadingMenu,
    CascadingLeftSubmenu,
    CascadingSubmenu,
    CascadingMenuItem
};
